import * as THREE from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import * as SkeletonUtils from 'three/addons/utils/SkeletonUtils.js';
import {
  CHARACTER_SCALE,
  CLASSES,
  UNIT_HALF_HEIGHT,
  auraDef,
  type AbilityDef,
  type AuraId,
  type AuraSnap,
  type ChatChannel,
  type ClassId,
} from '@arena/shared';
import { disposeObject } from './Fx';
import { auraSeconds } from './hud.svelte';
import type { Footfall } from './sounds';
import { WeaponTrail } from './WeaponTrail';

export interface UnitPose {
  pos: THREE.Vector3;
  yaw: number;
  vel: THREE.Vector3;
  grounded: boolean;
  dead: boolean;
  casting: boolean;
  /** Clip it holds while standing still, like a hub emote. */
  emote?: string;
}

const FADE = 0.15;
/** Kenney's attack and cast clips are snappy (0.2-0.7 s); shorter ones are slowed to last this long. */
const ONE_SHOT_SECONDS = 0.8;
/** A chat bubble lasts this long, plus a little per character. */
const BUBBLE_MS = 4000;
const BUBBLE_MS_PER_CHAR = 50;
const RUN_THRESHOLD = 4;
const WALK_THRESHOLD = 0.3;
/** Shorter hops than this, like stepping down a stair, don't count as landing. */
const LANDING_AIRTIME = 0.25;
const JUMP_SPEED = 2;
const TRAIL_SECONDS = 0.35;
/** Right fist center in arm-bone space (mirror x for the left). Kenney arms rest in a T-pose, pivoting at the shoulder. */
const HAND = new THREE.Vector3(-0.22, 0, 0.045);
interface HeldPose {
  rotation: number[];
  grip: number[];
  scale?: number;
}
/** Survival Kit tools share one pose: handle along +y from the origin, modeled smaller than the mini kits. */
const SURVIVAL_TOOL: HeldPose = {
  rotation: [Math.PI / 2, 0, 0],
  grip: [0, 0.05, 0],
  scale: 2,
};
/**
 * Holdable models by path under /models, posed for the right fist (the left mirrors across x).
 * rotation is an Euler in arm-bone space, where the arm runs along x, +y points away from the body and +z forward;
 * grip is the model-space point that sits in the fist; scale (default 1) multiplies the model.
 */
export const HELD_ITEMS = {
  'weapon-sword': { rotation: [-Math.PI / 2, 0, 0], grip: [0, 0.027, 0] },
  'weapon-spear': { rotation: [Math.PI / 2, 0, 0], grip: [0, 0.25, 0] },
  'dungeon/weapon-sword': {
    rotation: [Math.PI / 2, 0, 0],
    grip: [0, -0.05, 0],
  },
  'dungeon/weapon-spear': { rotation: [Math.PI / 2, 0, 0], grip: [0, 0.2, 0] },
  'dungeon/shield-round': {
    rotation: [-Math.PI / 2, 0, -Math.PI / 2],
    grip: [0, 0, -0.1],
  },
  'dungeon/shield-rectangle': {
    rotation: [-Math.PI / 2, 0, -Math.PI / 2],
    grip: [0, 0.15, -0.1],
  },
  'forest/weapon-bow': {
    rotation: [0, -Math.PI / 2, 0],
    grip: [-0.04, 0.24, 0.1],
  },
  'forest/weapon-arrow': { rotation: [0, 0, 0], grip: [0, 0, 0] },
  'survival/tool-axe': SURVIVAL_TOOL,
  'survival/tool-axe-upgraded': SURVIVAL_TOOL,
  'survival/tool-hammer': SURVIVAL_TOOL,
  'survival/tool-hammer-upgraded': SURVIVAL_TOOL,
  'survival/tool-hoe': SURVIVAL_TOOL,
  'survival/tool-hoe-upgraded': SURVIVAL_TOOL,
  'survival/tool-pickaxe': SURVIVAL_TOOL,
  'survival/tool-pickaxe-upgraded': SURVIVAL_TOOL,
  'survival/tool-shovel': SURVIVAL_TOOL,
  'survival/tool-shovel-upgraded': SURVIVAL_TOOL,
};
export type HeldItem = keyof typeof HELD_ITEMS;
export type HeldModels = Partial<Record<HeldItem, THREE.Object3D>>;
export interface Hands {
  right: HeldItem | null;
  left: HeldItem | null;
}
const SIDES = ['right', 'left'] as const;
type Side = (typeof SIDES)[number];

/** Each class's character model (by path under /models; all share the soldier's rig and clips) and gear. */
export const CLASS_LOOKS: Record<ClassId, { character: string; hands: Hands }> =
  {
    warrior: {
      character: 'dungeon/character-orc',
      hands: {
        right: 'survival/tool-axe-upgraded',
        left: 'dungeon/shield-round',
      },
    },
    mage: {
      character: 'graveyard/character-skeleton',
      hands: { right: 'weapon-spear', left: null },
    },
    priest: {
      character: 'graveyard/character-ghost',
      hands: { right: 'dungeon/weapon-spear', left: null },
    },
    rogue: {
      character: 'graveyard/character-vampire',
      hands: { right: 'weapon-sword', left: 'forest/weapon-arrow' },
    },
    hunter: {
      character: 'forest/character-archer',
      hands: { right: 'forest/weapon-bow', left: null },
    },
    paladin: {
      character: 'dungeon/character-human',
      hands: {
        right: 'survival/tool-hammer-upgraded',
        left: 'dungeon/shield-rectangle',
      },
    },
    druid: {
      character: 'graveyard/character-zombie',
      hands: { right: 'survival/tool-hoe-upgraded', left: null },
    },
    shaman: {
      character: 'graveyard/character-keeper',
      hands: { right: 'survival/tool-hammer', left: 'dungeon/shield-round' },
    },
  };

/** Shapeshift auras and the Cube Pet each one turns the unit into; pets are modeled at about character size. */
export const FORMS: Partial<Record<AuraId, { model: string; scale: number }>> =
  {
    'bear-form': { model: 'pets/animal-polar', scale: 1 },
    'travel-form': { model: 'pets/animal-deer', scale: 0.85 },
  };
export const FORM_MODELS = Object.values(FORMS).map((form) => form.model);
export const NO_HANDS: Hands = { right: null, left: null };
/** A placeholder: totems stand as a lantern whose green frame takes the totem's color. */
export const TOTEM_MODEL = 'graveyard/lantern-glass';
const TOTEM_SCALE = 2.5;
/** The lantern's palette, its frame recolored, one per totem color. */
const totemMaps = new Map<number, THREE.Texture>();

function totemMap(map: THREE.Texture, color: number): THREE.Texture {
  const cached = totemMaps.get(color);
  if (cached) return cached;
  const image = map.image as CanvasImageSource & {
    width: number;
    height: number;
  };
  const canvas = document.createElement('canvas');
  canvas.width = image.width;
  canvas.height = image.height;
  const context = canvas.getContext('2d')!;
  context.drawImage(image, 0, 0);
  const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
  const tint = new THREE.Color(color);
  const data = pixels.data;
  for (let i = 0; i < data.length; i += 4) {
    const [r, g, b] = [data[i], data[i + 1], data[i + 2]];
    if (g < r + 40 || g < b) continue;
    // The brightest frame green keeps the tint as is; darker greens shade it.
    const shade = Math.min(1.2, g / 196) * 255;
    data[i] = tint.r * shade;
    data[i + 1] = tint.g * shade;
    data[i + 2] = tint.b * shade;
  }
  context.putImageData(pixels, 0, 0);
  const tinted = map.clone();
  // A clone shares its image with the kit's colormap; setting `image` would tint every model.
  tinted.source = new THREE.Source(canvas);
  tinted.needsUpdate = true;
  totemMaps.set(color, tinted);
  return tinted;
}

/** A character in the scene: model, animation, target ring, aura visuals and a DOM nameplate. */
export class UnitView {
  readonly root = new THREE.Group();
  /** Invisible capsule-sized proxy used for click targeting. */
  readonly hitbox: THREE.Mesh;
  readonly nameplate: HTMLDivElement;
  /** Shown even for the local player, whose nameplate is hidden. */
  readonly bubble: HTMLDivElement;
  private bubbleUntil = 0;
  private model!: THREE.Object3D;
  private modelScale = CHARACTER_SCALE;
  private form: AuraId | null = null;
  private mixer!: THREE.AnimationMixer;
  private actions = new Map<string, THREE.AnimationAction>();
  private base: THREE.AnimationAction | null = null;
  private oneShot: { action: THREE.AnimationAction; until: number } | null =
    null;
  private targetRing: THREE.Mesh;
  private materials: THREE.MeshLambertMaterial[] = [];
  private auraFx = new Map<AuraVisual, THREE.Object3D>();
  private debuffs: HTMLDivElement;
  private debuffKey = '';
  private hpBar: HTMLDivElement;
  private castBar: HTMLDivElement;
  private castFill: HTMLDivElement;
  private tag: HTMLDivElement;
  private hand: THREE.Object3D | undefined;
  /** Each hand's item and the stretch of it a weapon trail sweeps, in its model space. */
  private blades: Partial<
    Record<
      Side,
      { item: THREE.Object3D; hilt: THREE.Vector3; tip: THREE.Vector3 }
    >
  > = {};
  private trails: Partial<
    Record<Side, { classId: ClassId; trail: WeaponTrail }>
  > = {};
  private stealthed = false;
  private flashColor = new THREE.Color();
  private flashLeft = 0;
  private flashLength = 1;
  private time = 0;
  /** Each footstep, jump and landing. */
  onFootfall: ((footfall: Footfall) => void) | null = null;
  private stride: number | null = null;
  private airtime = 0;
  totem = false;

  constructor(
    readonly id: string,
    readonly classId: ClassId,
    name: string,
    friendly: boolean,
    isSelf: boolean,
    private character: GLTF,
    private items: HeldModels,
    overlay: HTMLElement,
    private hands: Hands = CLASS_LOOKS[classId].hands,
    /** Loaded models by path, where `FORMS` models are looked up. */
    private models: Record<string, GLTF> = {},
  ) {
    this.setBody(character, CHARACTER_SCALE, hands);

    this.targetRing = flatRing(0.7, 0.9, friendly ? 0x4caf50 : 0xe04a3a, 0.9);
    this.targetRing.visible = false;
    this.root.add(this.targetRing);

    this.hitbox = new THREE.Mesh(
      new THREE.CylinderGeometry(0.6, 0.6, 1.6, 8),
      new THREE.MeshBasicMaterial({ visible: false }),
    );
    this.hitbox.position.y = 0.8;
    this.hitbox.userData.unitId = id;
    this.root.add(this.hitbox);

    this.nameplate = document.createElement('div');
    this.nameplate.className = `nameplate ${friendly ? 'friendly' : 'enemy'}`;
    this.debuffs = document.createElement('div');
    this.debuffs.className = 'np-debuffs';
    const label = document.createElement('div');
    label.className = 'np-name';
    label.textContent = name;
    label.style.color = CLASSES[classId].color;
    this.tag = document.createElement('div');
    this.tag.className = 'np-tag';
    const hp = document.createElement('div');
    hp.className = 'np-hp';
    this.hpBar = document.createElement('div');
    hp.appendChild(this.hpBar);
    this.castBar = document.createElement('div');
    this.castBar.className = 'np-cast';
    this.castFill = document.createElement('div');
    this.castBar.appendChild(this.castFill);
    this.nameplate.append(this.debuffs, label, this.tag, hp, this.castBar);
    if (isSelf) this.nameplate.style.display = 'none';
    overlay.appendChild(this.nameplate);
    this.bubble = document.createElement('div');
    this.bubble.style.display = 'none';
    overlay.appendChild(this.bubble);
  }

  /** Swaps in a model with its own clips: the class character, or a shapeshift's pet. */
  private setBody(gltf: GLTF, scale: number, hands: Hands): void {
    if (this.model) {
      this.root.remove(this.model);
      this.mixer.stopAllAction();
      for (const material of this.materials) material.dispose();
    }
    this.materials = [];
    this.model = SkeletonUtils.clone(gltf.scene);
    this.modelScale = scale;
    this.model.scale.setScalar(scale);
    this.model.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      child.castShadow = true;
      const material = (child.material as THREE.MeshLambertMaterial).clone();
      child.material = material;
      this.materials.push(material);
    });
    this.root.add(this.model);
    this.blades = {};
    this.attachHeld(this.items, hands);
    this.hand = this.model.getObjectByName('arm-right');

    this.mixer = new THREE.AnimationMixer(this.model);
    this.actions = new Map(
      gltf.animations.map((clip) => [clip.name, this.mixer.clipAction(clip)]),
    );
    // Cube Pets call their sprint `run`.
    const run = this.actions.get('run');
    if (run && !this.actions.has('sprint')) this.actions.set('sprint', run);
    this.base = null;
    this.oneShot = null;
    this.setBase('idle');
  }

  private attachHeld(items: HeldModels, hands: Hands): void {
    for (const side of SIDES) {
      const name = hands[side];
      const template = name && items[name];
      const arm = this.model.getObjectByName(`arm-${side}`);
      if (!template || !arm) continue;
      const mirror = side === 'left' ? -1 : 1;
      const pose: HeldPose = HELD_ITEMS[name];
      const [rx, ry, rz] = pose.rotation;
      const [gx, gy, gz] = pose.grip;
      const scale = pose.scale ?? 1;
      const item = template.clone();
      item.rotation.set(rx, ry * mirror, rz * mirror);
      item.scale.set(scale * mirror, scale, scale);
      item.position
        .set(-gx * mirror, -gy, -gz)
        .multiplyScalar(scale)
        .applyQuaternion(item.quaternion)
        .add(new THREE.Vector3(HAND.x * mirror, HAND.y, HAND.z));
      arm.add(item);
      this.blades[side] = { item, ...bladeOf(template, pose) };
    }
  }

  /** World positions of a hand's item's hilt and tip, for a weapon trail; null with nothing in it. */
  weaponEdge(side: Side): [THREE.Vector3, THREE.Vector3] | null {
    const blade = this.blades[side];
    if (!blade) return null;
    const { item, hilt, tip } = blade;
    item.updateWorldMatrix(true, false);
    return [item.localToWorld(hilt.clone()), item.localToWorld(tip.clone())];
  }

  /** Built with `TOTEM_MODEL` as its character: sizes the lantern and colors its frame. */
  makeTotem(color: number): void {
    this.totem = true;
    this.modelScale = TOTEM_SCALE;
    this.model.scale.setScalar(TOTEM_SCALE);
    for (const material of this.materials)
      if (material.map) material.map = totemMap(material.map, color);
  }

  setTargeted(targeted: boolean): void {
    this.targetRing.visible = targeted;
    this.nameplate.classList.toggle('targeted', targeted);
  }

  /** Wears a weapon trail in a class's color on each hand, or none; a hand holding nothing shows none. */
  setTrails(right: ClassId | undefined, left: ClassId | undefined): void {
    for (const [side, classId] of [
      ['right', right],
      ['left', left],
    ] as const) {
      if (this.trails[side]?.classId === classId) continue;
      this.trails[side]?.trail.dispose();
      delete this.trails[side];
      if (!classId) continue;
      const trail = new WeaponTrail(
        CLASSES[classId].color,
        'ribbon',
        TRAIL_SECONDS,
      );
      this.trails[side] = { classId, trail };
      this.root.add(trail.mesh);
    }
  }

  setStealthed(stealthed: boolean): void {
    this.stealthed = stealthed;
    for (const material of this.materials) {
      if (material.transparent === stealthed) continue;
      material.transparent = stealthed;
      material.opacity = stealthed ? 0.35 : 1;
      // Opaque and transparent materials compile to different shaders.
      material.needsUpdate = true;
    }
  }

  say(text: string, channel: ChatChannel): void {
    this.bubble.textContent = text;
    this.bubble.className = `bubble ${channel}`;
    this.bubbleUntil =
      performance.now() + BUBBLE_MS + text.length * BUBBLE_MS_PER_CHAR;
  }

  get speaking(): boolean {
    return performance.now() < this.bubbleUntil;
  }

  setTag(tag = ''): void {
    if (this.tag.textContent !== tag) this.tag.textContent = tag;
  }

  setHealth(fraction: number): void {
    this.hpBar.style.width = `${Math.max(0, Math.min(1, fraction)) * 100}%`;
  }

  setCast(progress: number | null, interrupted = false): void {
    this.castBar.style.visibility = progress === null ? 'hidden' : 'visible';
    this.castBar.classList.toggle('interrupted', interrupted);
    if (progress !== null) this.castFill.style.width = `${progress * 100}%`;
  }

  /** Shows a visual for each aura that has one, and harmful ones above the nameplate. */
  setAuras(auras: Pick<AuraSnap, 'auraId' | 'remaining'>[]): void {
    const auraIds = auras.map((a) => a.auraId);
    const form =
      auraIds.find((id) => {
        const model = FORMS[id]?.model;
        return model !== undefined && this.models[model] !== undefined;
      }) ?? null;
    if (form !== this.form) {
      this.form = form;
      const shape = form && FORMS[form];
      if (shape) this.setBody(this.models[shape.model], shape.scale, NO_HANDS);
      else this.setBody(this.character, CHARACTER_SCALE, this.hands);
    }
    this.setDebuffs(auras.filter((a) => auraDef(a.auraId).harmful));
    const wanted = new Set<AuraVisual>();
    for (const id of auraIds) {
      const kind = auraVisual(id);
      if (kind) wanted.add(kind);
    }
    for (const [kind, object] of this.auraFx) {
      if (wanted.has(kind)) continue;
      this.root.remove(object);
      disposeObject(object);
      this.auraFx.delete(kind);
    }
    for (const kind of wanted) {
      if (this.auraFx.has(kind)) continue;
      const object = makeAuraVisual(kind);
      this.auraFx.set(kind, object);
      this.root.add(object);
    }
    const polymorphed = auraIds.includes('polymorph');
    this.model.scale.setScalar(this.modelScale * (polymorphed ? 0.45 : 1));
  }

  private setDebuffs(debuffs: Pick<AuraSnap, 'auraId' | 'remaining'>[]): void {
    const key = debuffs.map((a) => a.auraId).join();
    if (key !== this.debuffKey) {
      this.debuffKey = key;
      this.debuffs.replaceChildren(
        ...debuffs.map((a) => {
          const icon = document.createElement('div');
          icon.className = 'np-debuff';
          const img = document.createElement('img');
          img.src = `/icons/${auraDef(a.auraId).icon ?? a.auraId}.webp`;
          icon.append(img, document.createElement('span'));
          return icon;
        }),
      );
    }
    debuffs.forEach((a, i) => {
      const time = this.debuffs.children[i].lastElementChild!;
      const text = a.remaining > 0 ? auraSeconds(a.remaining) : '';
      if (time.textContent !== text) time.textContent = text;
    });
  }

  /** World position of the right fist. */
  handPosition(out: THREE.Vector3): THREE.Vector3 {
    if (!this.hand)
      return out.copy(this.root.position).setY(this.root.position.y + 1);
    this.hand.updateWorldMatrix(true, false);
    return this.hand.localToWorld(out.copy(HAND));
  }

  /** Tints the model briefly, fading out. */
  flash(color: number, seconds: number): void {
    this.flashColor.set(color);
    this.flashLeft = this.flashLength = seconds;
  }

  playOnce(name: string): void {
    const action = this.actions.get(name);
    if (!action) return;
    this.oneShot?.action.fadeOut(0.05);
    action.reset().setLoop(THREE.LoopOnce, 1).fadeIn(0.05).play();
    const duration = action.getClip().duration;
    action.timeScale = Math.min(1, duration / ONE_SHOT_SECONDS);
    action.clampWhenFinished = true;
    this.base?.fadeOut(0.05);
    this.oneShot = {
      action,
      until: this.time + (duration * 0.9) / action.timeScale,
    };
  }

  update(dt: number, pose: UnitPose): void {
    this.time += dt;
    this.root.position.set(
      pose.pos.x,
      pose.pos.y - UNIT_HALF_HEIGHT,
      pose.pos.z,
    );
    // Kenney characters face +z; yaw 0 faces -z.
    this.model.rotation.y = pose.yaw + Math.PI;
    for (const fx of this.auraFx.values()) {
      fx.rotation.y += dt * 1.5;
      fx.userData.tick?.(this.time);
    }
    if (this.flashLeft > 0) {
      this.flashLeft = Math.max(0, this.flashLeft - dt);
      const strength = (0.7 * this.flashLeft) / this.flashLength;
      for (const material of this.materials)
        material.emissive.copy(this.flashColor).multiplyScalar(strength);
    }

    const speed = Math.hypot(pose.vel.x, pose.vel.z);
    let next = 'idle';
    if (pose.dead) next = 'die';
    else if (!pose.grounded) next = pose.vel.y > 0 ? 'jump' : 'fall';
    else if (speed > RUN_THRESHOLD) next = 'sprint';
    else if (speed > WALK_THRESHOLD) next = 'walk';
    else if (pose.casting) next = 'holding-both';
    else if (pose.emote) next = pose.emote;
    this.setBase(next);

    if (this.base && (next === 'sprint' || next === 'walk')) {
      const forward =
        -Math.sin(pose.yaw) * pose.vel.x - Math.cos(pose.yaw) * pose.vel.z;
      this.base.timeScale = forward < -0.1 ? -1 : 1;
    }

    if (this.oneShot && (this.time >= this.oneShot.until || pose.dead)) {
      this.oneShot.action.fadeOut(FADE);
      this.oneShot = null;
      this.base?.reset().fadeIn(FADE).play();
    }
    this.mixer.update(dt);
    for (const side of SIDES) {
      const trail = this.trails[side]?.trail;
      if (!trail) continue;
      trail.mesh.visible = !this.stealthed;
      trail.update(dt, this.weaponEdge(side), this.root.position);
    }
    if (!pose.dead) this.footfalls(next, pose, dt);
  }

  /** Feet strike a quarter and three quarters of the way through the walk and sprint loops. */
  private footfalls(base: string, pose: UnitPose, dt: number): void {
    if (!pose.grounded) {
      if (this.airtime === 0 && pose.vel.y > JUMP_SPEED)
        this.onFootfall?.('jump');
      this.airtime += dt;
    } else {
      if (this.airtime > LANDING_AIRTIME) this.onFootfall?.('land');
      this.airtime = 0;
    }
    const action =
      (base === 'walk' || base === 'sprint') && !this.oneShot
        ? this.base
        : null;
    if (!action) {
      this.stride = null;
      return;
    }
    const stride =
      Math.floor((action.time / action.getClip().duration) * 2 + 0.5) % 2;
    if (this.stride !== null && stride !== this.stride)
      this.onFootfall?.(base === 'sprint' ? 'run' : 'walk');
    this.stride = stride;
  }

  private setBase(name: string): void {
    const action = this.actions.get(name);
    if (!action || action === this.base) return;
    const previous = this.base;
    this.base = action;
    action.reset();
    if (name === 'die') {
      action.setLoop(THREE.LoopOnce, 1);
      action.clampWhenFinished = true;
    } else {
      action.setLoop(THREE.LoopRepeat, Infinity);
    }
    action.timeScale = 1;
    // reset() re-enables an action still in the mixer at full weight, and nothing would fade it out.
    if (this.oneShot && name !== 'die') {
      action.stop();
      return;
    }
    action.fadeIn(FADE).play();
    previous?.fadeOut(FADE);
  }

  dispose(): void {
    this.nameplate.remove();
    this.bubble.remove();
    this.mixer.stopAllAction();
    for (const material of this.materials) material.dispose();
    for (const fx of this.auraFx.values()) disposeObject(fx);
    for (const side of SIDES) this.trails[side]?.trail.dispose();
  }
}

/** Along a held model's longest axis, from 30% of the way out from the grip to its far end. */
function bladeOf(template: THREE.Object3D, pose: HeldPose) {
  template.updateWorldMatrix(false, true);
  const box = new THREE.Box3().setFromObject(template);
  const size = box.getSize(new THREE.Vector3());
  const axis =
    size.x >= size.y && size.x >= size.z ? 'x' : size.y >= size.z ? 'y' : 'z';
  const grip = new THREE.Vector3().fromArray(pose.grip)[axis];
  const far =
    Math.abs(box.max[axis] - grip) >= Math.abs(box.min[axis] - grip)
      ? box.max[axis]
      : box.min[axis];
  const tip = box.getCenter(new THREE.Vector3());
  tip[axis] = far;
  const hilt = tip.clone();
  hilt[axis] = THREE.MathUtils.lerp(grip, far, 0.3);
  return { hilt, tip };
}

/** Every material a unit can wear: each model opaque and stealthed, and the aura visuals'. */
export function unitShaders(models: GLTF[]): THREE.Object3D {
  const group = new THREE.Group();
  for (const gltf of models)
    for (const transparent of [false, true]) {
      const model = SkeletonUtils.clone(gltf.scene);
      model.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        child.material = (child.material as THREE.Material).clone();
        child.material.transparent = transparent;
      });
      group.add(model);
    }
  for (const material of [unlit(0), unlit(0, 0.5), glass(0, 0.5)])
    group.add(new THREE.Mesh(new THREE.BufferGeometry(), material));
  return group;
}

/** The one-shot a unit plays when an ability goes off. */
export function castAnimation(ability: AbilityDef): string {
  if (ability.target === 'enemy' && !ability.range)
    return ability.effects.some((effect) => effect.type === 'interrupt')
      ? 'attack-kick-right'
      : 'attack-melee-right';
  return ability.target === 'self' ? 'interact-right' : 'holding-right-shoot';
}

function flatRing(
  inner: number,
  outer: number,
  color: number,
  opacity: number,
): THREE.Mesh {
  const ring = new THREE.Mesh(
    new THREE.RingGeometry(inner, outer, 40),
    new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.04;
  return ring;
}

type AuraVisual =
  | 'ice-block'
  | 'shield'
  | 'divine-shield'
  | 'shield-wall'
  | 'ice-barrier'
  | 'stun'
  | 'dazed'
  | 'root'
  | 'frost-nova'
  | 'fear'
  | 'silence'
  | 'renew'
  | 'burning'
  | 'poison'
  | 'shadow'
  | 'bleed'
  | 'chilled'
  | 'wound'
  | 'pain-suppression'
  | 'freedom'
  | 'sprint'
  | 'rejuvenation'
  | 'moonfire'
  | 'entangling-roots'
  | 'cyclone'
  | 'barkskin';

function auraVisual(id: AuraId): AuraVisual | null {
  switch (id) {
    case 'ice-block':
    case 'divine-shield':
    case 'shield-wall':
    case 'ice-barrier':
    case 'frost-nova':
    case 'renew':
    case 'chilled':
    case 'pain-suppression':
    case 'sprint':
    case 'rejuvenation':
    case 'moonfire':
    case 'entangling-roots':
    case 'cyclone':
    case 'barkskin':
      return id;
    case 'regrowth':
      return 'rejuvenation';
    case 'freezing-trap':
      return 'ice-block';
    case 'power-word-shield':
      return 'shield';
    case 'cheap-shot':
    case 'hammer-of-justice':
    case 'kidney-shot':
    case 'gouge':
    case 'scatter-shot':
      return 'stun';
    case 'concussive-shot':
      return 'dazed';
    case 'charge-root':
      return 'root';
    case 'psychic-scream':
    case 'intimidating-shout':
      return 'fear';
    case 'silencing-shot':
      return 'silence';
    case 'fireball':
      return 'burning';
    case 'serpent-sting':
      return 'poison';
    case 'shadow-word-pain':
      return 'shadow';
    case 'rend':
      return 'bleed';
    case 'mortal-wound':
    case 'aimed-shot':
      return 'wound';
    case 'blessing-of-freedom':
      return 'freedom';
    default:
      return null;
  }
}

const unlit = (color: number, opacity = 1) =>
  new THREE.MeshBasicMaterial({
    color,
    transparent: opacity < 1,
    opacity,
    depthWrite: false,
  });

const glass = (color: number, opacity: number) =>
  new THREE.MeshStandardMaterial({
    color,
    transparent: true,
    opacity,
    roughness: 0.2,
    depthWrite: false,
  });

function bubble(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
): THREE.Mesh {
  const mesh = new THREE.Mesh(geometry, material);
  mesh.position.y = 0.8;
  return mesh;
}

/** `count` small shapes circling at `height`. */
function orbit(
  color: number,
  count: number,
  height: number,
  radius: number,
): THREE.Group {
  const group = new THREE.Group();
  const geometry = new THREE.OctahedronGeometry(0.1);
  const material = unlit(color);
  for (let i = 0; i < count; i++) {
    const mesh = new THREE.Mesh(geometry, material);
    const angle = (i / count) * Math.PI * 2;
    mesh.position.set(
      Math.cos(angle) * radius,
      height,
      Math.sin(angle) * radius,
    );
    group.add(mesh);
  }
  return group;
}

/** Motes drifting from height `from` to `to` on a loop. */
function motes(
  color: number,
  count: number,
  from: number,
  to: number,
  radius = 0.45,
  size = 0.05,
): THREE.Group {
  const group = new THREE.Group();
  const geometry = new THREE.OctahedronGeometry(size);
  const material = unlit(color, 0.9);
  const parts = Array.from({ length: count }, (_, i) => {
    const mesh = new THREE.Mesh(geometry, material);
    const angle = i * 2.4;
    const r = radius * (0.6 + 0.4 * ((i * 7) % 5) * 0.25);
    mesh.position.set(Math.cos(angle) * r, from, Math.sin(angle) * r);
    group.add(mesh);
    return { mesh, phase: i / count };
  });
  group.userData.tick = (time: number) => {
    for (const { mesh, phase } of parts) {
      const t = (time * 0.6 + phase) % 1;
      mesh.position.y = from + (to - from) * t;
      mesh.scale.setScalar(Math.sin(Math.PI * t));
    }
  };
  return group;
}

function halo(
  color: number,
  radius: number,
  height: number,
  opacity = 1,
): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.TorusGeometry(radius, 0.04, 6, 28),
    unlit(color, opacity),
  );
  mesh.rotation.x = Math.PI / 2;
  mesh.position.y = height;
  return mesh;
}

function makeAuraVisual(kind: AuraVisual): THREE.Object3D {
  switch (kind) {
    case 'ice-block':
      return bubble(
        new THREE.IcosahedronGeometry(1.1, 0),
        glass(0x9fe3ff, 0.55),
      );
    case 'shield':
      return bubble(
        new THREE.SphereGeometry(1.05, 20, 14),
        glass(0xfff0a0, 0.25),
      );
    case 'divine-shield':
      return bubble(
        new THREE.SphereGeometry(1.2, 24, 16),
        unlit(0xffd45a, 0.3),
      );
    case 'shield-wall':
      return bubble(
        new THREE.IcosahedronGeometry(1.1, 1),
        glass(0xa9b8c8, 0.3),
      );
    case 'ice-barrier':
      return bubble(
        new THREE.IcosahedronGeometry(1.05, 1),
        glass(0xcff4ff, 0.3),
      );
    case 'stun':
      return orbit(0xffe066, 3, 1.75, 0.35);
    case 'dazed':
      return orbit(0x8fb8ff, 4, 1.65, 0.4);
    case 'root':
      return flatRing(0.3, 0.9, 0x7fd8ff, 0.7);
    case 'frost-nova': {
      const group = new THREE.Group();
      const geometry = new THREE.ConeGeometry(0.12, 0.6, 5);
      const material = glass(0xbfeeff, 0.8);
      for (let i = 0; i < 7; i++) {
        const spike = new THREE.Mesh(geometry, material);
        const angle = (i / 7) * Math.PI * 2;
        spike.position.set(Math.cos(angle) * 0.6, 0.25, Math.sin(angle) * 0.6);
        spike.rotation.set(Math.sin(angle) * 0.4, 0, -Math.cos(angle) * 0.4);
        group.add(spike);
      }
      group.add(flatRing(0.2, 0.8, 0x7fd8ff, 0.5));
      return group;
    }
    case 'fear':
      return halo(0xa04ae0, 0.35, 1.8);
    case 'silence': {
      const group = new THREE.Group();
      group.add(halo(0xb07cff, 0.25, 1.85));
      const bar = new THREE.Mesh(
        new THREE.BoxGeometry(0.5, 0.05, 0.05),
        unlit(0xb07cff),
      );
      bar.position.y = 1.85;
      bar.rotation.y = Math.PI / 4;
      group.add(bar);
      return group;
    }
    case 'renew':
      return motes(0xfff0a0, 8, 0.1, 1.7);
    case 'burning':
      return motes(0xff7a2a, 10, 0.4, 1.8, 0.35, 0.06);
    case 'poison':
      return motes(0x6ad16a, 8, 0.2, 1.4, 0.4, 0.06);
    case 'shadow':
      return motes(0x5a2a96, 8, 1.6, 0.3, 0.4, 0.07);
    case 'bleed':
      return motes(0xa00d0d, 6, 1.1, 0.05, 0.3, 0.045);
    case 'chilled':
      return motes(0xbfeeff, 8, 0.05, 0.6, 0.55, 0.05);
    case 'wound': {
      const group = new THREE.Group();
      for (let i = 0; i < 3; i++) {
        const arc = new THREE.Mesh(
          new THREE.RingGeometry(0.6, 0.7, 16, 1, (i * Math.PI * 2) / 3, 1.4),
          unlit(0x7a0f0f, 0.7),
        );
        arc.rotation.x = -Math.PI / 2;
        arc.position.y = 0.05;
        group.add(arc);
      }
      return group;
    }
    case 'pain-suppression':
      return halo(0xe8eef8, 0.6, 0.9, 0.7);
    case 'freedom':
      return motes(0xffb060, 10, 0.05, 0.9, 0.6, 0.05);
    case 'sprint':
      return motes(0xb59a78, 6, 0.05, 0.4, 0.4, 0.08);
    case 'rejuvenation':
      return motes(0x9ef07a, 8, 0.1, 1.7);
    case 'moonfire':
      return motes(0xc8b8ff, 6, 2, 0.6, 0.35, 0.05);
    case 'entangling-roots': {
      const group = new THREE.Group();
      const geometry = new THREE.ConeGeometry(0.07, 0.7, 4);
      const material = unlit(0x6b4a24);
      for (let i = 0; i < 6; i++) {
        const vine = new THREE.Mesh(geometry, material);
        const angle = (i / 6) * Math.PI * 2;
        vine.position.set(Math.cos(angle) * 0.45, 0.3, Math.sin(angle) * 0.45);
        vine.rotation.set(-Math.sin(angle) * 0.5, 0, Math.cos(angle) * 0.5);
        group.add(vine);
      }
      group.add(flatRing(0.2, 0.75, 0x4f9a3a, 0.5));
      return group;
    }
    case 'cyclone': {
      const group = new THREE.Group();
      for (const [height, radius] of [
        [0.3, 0.45],
        [0.9, 0.65],
        [1.5, 0.85],
      ])
        group.add(
          halo(0xe6efe6, radius, height, 0.5),
          orbit(0x8fd17a, 3, height, radius),
        );
      return group;
    }
    case 'barkskin':
      return bubble(
        new THREE.IcosahedronGeometry(1.05, 0),
        glass(0x8a6a3a, 0.3),
      );
  }
}
